<template>
  <div
    class="card-home"
    :style="backgroundStyle"
  >
    <div class="card-home__info">
      <p class="card-home__title">
        {{ title }}
      </p>
      <p
        class="card-home__subtitle"
        v-html="subtitle"
      />
      <div class="card-home__actions">
        <atomic-card-tooltip
          v-if="showTooltip"
          :text="tooltipText"
          :show-icon="false"
        >
          <button-base
            tag-name="button"
            :is-disabled="button?.isDisabled"
            :type="btnType"
            size="md"
            class="card-home__join-btn"
            @click="handleClickButton"
          >
            {{ button?.label }}

            <template #icon-right>
              <slot name="button-icon-right" />
            </template>
          </button-base>
        </atomic-card-tooltip>

        <button-base
          v-else
          tag-name="button"
          :is-disabled="button?.isDisabled"
          :type="btnType"
          size="md"
          class="card-home__join-btn"
          @click="handleClickButton"
        >
          {{ button?.label }}

          <template #icon-right>
            <slot name="button-icon-right" />
          </template>
        </button-base>

        <div
          v-if="isSwitcherEnabled"
          class="card-home__switcher"
        >
          <span
            v-if="switcherText"
            class="card-home__switcher-text"
          >
            {{ switcherText }}
          </span>
          <atomic-switch
            :model-value="switcherValue"
            @update:model-value="handleSwitcherChange"
          />
        </div>

        <atomic-socials class="card-home__socials" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ModalName } from '@skeleton/consts/modals';
import { useWindowSize } from '@vueuse/core';
import type { IHomePage } from '~/types';

type IHomeCardProps = IHomePage['banner'] & {
  btnType?: 'primary' | 'secondary' | 'tertiary';
  showTooltip?: boolean;
  button?: {
    isDisabled: boolean;
  };
  tooltipText?: string;
  backgroundGradient?: string;
  backgroundColor?: string;
  isSwitcherEnabled?: boolean;
  switcherText?: string;
  switcherValue?: boolean;
  isWithoutImageOnMobile?: boolean;
};

const props = withDefaults(defineProps<IHomeCardProps>(), {
  btnType: 'primary',
  showTooltip: false,
  isSwitcherEnabled: false,
  switcherValue: false,
  isWithoutImageOnMobile: false,
});

const emit = defineEmits<{ 'update:switcherValue': [value: boolean] }>();
const handleSwitcherChange = (value: boolean): void => {
  emit('update:switcherValue', value);
};

const { openModalSync } = useModalStore();

const profileStore = useProfileStore();
const { isLoggedIn } = storeToRefs(profileStore);
const { openWalletModal } = useTransactionStore();
const handleClickButton = (): void => {
  if (isLoggedIn.value) openWalletModal('deposit');
  else openModalSync(ModalName.SIGN_IN);
};

const imageSrc = computed(() => {
  const src = props.images?.backgroundImage;
  return src ? src.replace(/\.\w+$/, '.webp') : '';
});

const { width } = useWindowSize();

const backgroundStyle = computed(() => {
  const styles: Record<string, string> = {};

  if (props.backgroundGradient) {
    styles['background'] = props.backgroundGradient;
  }

  if (props.backgroundColor) {
    styles['background-color'] = props.backgroundColor;
  }

  const shouldShowImage = imageSrc.value && (width.value >= 768 || !props.isWithoutImageOnMobile);

  if (shouldShowImage) {
    if (props.backgroundGradient) {
      styles['background'] = `url(${imageSrc.value}) no-repeat bottom right / auto 100%, ${props.backgroundGradient}`;
    }
    else {
      styles['background-image'] = `url(${imageSrc.value})`;
    }
  }

  return styles;
});
</script>

<style src="~/assets/styles/components/card/home.scss" lang="scss" />
